import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  styled,
  Typography,
} from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import { useQueries, useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  handleFileDownload,
  onDownloadCostImpactClick,
} from 'helpers/filesHelpers/downloadFiles';
import { getCostImpactFilesQueryOptions } from 'queries/fileQueries';
import { createGetSiteOwnerDetailsOptions } from 'queries/siteOwnerQueries';
import {
  createGetOverviewOptions,
  createGetOverviewStepsOptions,
} from '../../queries/overviewQueries';
import { getCostImpactOnProducts } from '../../queries/SummaryQueries';
import {
  boldFont,
  summaryAccordionIcon,
  SummaryAccordionStyle,
} from '../../styles';
import Button from '../shared/Button';
import MailTo from '../SummaryPage/MailTo';
import DeletePecr from './DeletePecr';
import EditResource from './EditResource';
import ReminderEmail from './ReminderEmail';
import TrackingFileStepper from './TrackingFileStepper';

function TrackingFileDetailsFilter() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  // const userInfo = useSelector(state => state.userInfo);
  const pecrId = searchParams.get('id');
  const { data } = useQuery(
    createGetOverviewStepsOptions({ pecrId: pecrId }, { enabled: !!pecrId })
  );
  const { data: overviewData } = useQuery(
    createGetOverviewOptions({ pecrId }, { enabled: !!pecrId })
  );
  const siteData = data && Object.groupBy(data, item => item.SiteName);
  const siteIds = data ? Object.groupBy(data, item => item.SiteID) : {};
  const costImpactQueries = useQueries({
    queries: Object.keys(siteIds).map(id => ({
      queryKey: ['cost-impacts', id, pecrId],
      queryFn: () =>
        getCostImpactOnProducts({ pecrId: pecrId, siteId: Number(id) }),
    })),
    enabled: !!Object.keys(siteIds).length,
  });

  // const isCreator = overviewData?.UserID === userInfo?.userID;

  const costImpactData = costImpactQueries.map(query => query.data);
  return (
    <Accordion sx={SummaryAccordionStyle} elevation={0}>
      <AccordionSummary
        expandIcon={<ArrowDropDownIcon sx={summaryAccordionIcon} />}
        aria-controls="drivers-content"
        id="drivers-header"
      >
        <Grid style={boldFont}>{t('Details')}</Grid>
      </AccordionSummary>
      <AccordionDetails>
        {siteData &&
          overviewData &&
          costImpactData &&
          !costImpactData.some(item => item === undefined) &&
          Object.keys(siteData).map((site, index) => (
            <TrackingFileDetailsRow
              key={index}
              site={site}
              data={siteData[site]}
              costImpact={costImpactData.find(
                item => item?.siteId === Number(siteData[site][0]?.SiteID)
              )}
              siteId={siteData[site][0]?.SiteID || null}
              pecrId={pecrId}
              overviewData={overviewData}
              // isCreator={isCreator}
            />
          ))}
      </AccordionDetails>
    </Accordion>
  );
}

TrackingFileDetailsRow.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      Step: PropTypes.string,
      Effort: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      Cost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      SourceTable: PropTypes.string,
      UserName: PropTypes.string,
      productCostImpact: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    })
  ).isRequired,
  costImpact: PropTypes.object,
  site: PropTypes.string.isRequired,
  siteId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.null,
  ]),
  pecrId: PropTypes.string,
  // isCreator: PropTypes.bool,
};

function TrackingFileDetailsRow({
  data,
  costImpact = null,
  site,
  siteId,
  pecrId,
  overviewData,
  // isCreator,
}) {
  const userInfo = useSelector(state => state.userInfo);
  const { data: costImpactFiles } = useQuery(
    getCostImpactFilesQueryOptions({ pecrId: pecrId, siteId: siteId }, {})
  );

  const { data: siteOwnerDetails } = useQuery(
    createGetSiteOwnerDetailsOptions(
      { pecrId, siteId },
      { enabled: !!pecrId && !!siteId }
    )
  );

  function disableEditAndDeleteButton() {
    const siteCoordinatorStep = data.find(
      item => item.SourceTable === 'scReview'
    );
    const siteOwnerStep = data.find(item => item.SourceTable === 'soReview');
    const hasPermission =
      userInfo?.userEmail?.toLowerCase() ===
        siteCoordinatorStep?.Email?.toLowerCase() ||
      userInfo?.name?.toLowerCase() ===
        siteCoordinatorStep?.UserName?.toLowerCase() ||
      userInfo?.userEmail?.toLowerCase() ===
        siteOwnerStep?.Email?.toLowerCase() ||
      userInfo?.name?.toLowerCase() === siteOwnerStep?.UserName?.toLowerCase();
    return !hasPermission;
  }

  // function getSiteOwner(steps) {
  //   const ownerStep = steps.find(step => step.SourceTable === 'soReview');
  //   return ownerStep ? ownerStep.UserName : '-';
  // }
  function getTotalEffort(data, step) {
    const estimationSteps = data.filter(item => item.Step === step);
    return estimationSteps.reduce(
      (total, item) => total + Number(item.Effort || 0),
      0
    );
  }
  function getTotalCost(data, step) {
    const costSteps = data.filter(item => item.Step === step);
    return costSteps.reduce((total, step) => total + Number(step.Cost || 0), 0);
  }
  function getPreOrSuffix(data, type) {
    const text = data.find(
      item =>
        item[type] !== '' && item[type] !== null && item[type] !== undefined
    );
    return text ? text[type] : '';
  }
  function getEmails(data) {
    const emailArray = data
      .map(item => item['Email'])
      .filter(email => email && email.trim() !== '');
    return Array.from(new Set(emailArray));
  }
  const IconContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
  });

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns:
          '0.8fr 2fr 1fr 1fr 1fr 1fr 1fr 1fr auto auto auto auto auto',
        gap: '12px',
        marginBottom: '12px',
        padding: '12px',
        backgroundColor: '#fff',
        borderRadius: '4px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        alignItems: 'center',
      }}
    >
      <TrackingFileDetailRowItem title="Site" value={site} isButton />
      <TrackingFileDetailRowItem
        title="Steps"
        value={<TrackingFileStepper data={data} site={site} />}
      />
      <TrackingFileDetailRowItem
        title="Site Owner"
        value={siteOwnerDetails ? siteOwnerDetails.OwnerName : '-'}
      />
      <TrackingFileDetailRowItem
        title="Estimated Effort"
        value={getTotalEffort(data, 'Estimates')}
        suffix={' hrs'}
      />
      <TrackingFileDetailRowItem
        title="Estimated Cost"
        value={getTotalCost(data, 'Estimates')}
        prefix={getPreOrSuffix(data, 'Currency')}
      />
      <TrackingFileDetailRowItem
        title="Actual Effort"
        value={getTotalEffort(data, 'Execution')}
        suffix={' hrs'}
      />
      <TrackingFileDetailRowItem
        title="Actual Cost"
        value={getTotalCost(data, 'Execution')}
        prefix={getPreOrSuffix(data, 'Currency')}
      />
      <TrackingFileDetailRowItem
        title="Product Cost Impact"
        value={costImpact.data.costImpactOnProducts}
      />
      <TrackingFileDetailRowItem
        title="Cost Impact File"
        value={
          costImpactFiles && costImpactFiles.length > 0
            ? costImpactFiles
            : ['N/A']
        }
        isFile={true}
        pecrId={pecrId}
        siteId={siteId}
      />
      <IconContainer>
        <MailTo
          data={data && getEmails(data)}
          pecrId={pecrId || ''}
          rawData={true}
          title={overviewData?.Title || ''}
        />
      </IconContainer>
      <EditResource
        disabled={disableEditAndDeleteButton()}
        siteId={siteId}
        pecrId={pecrId}
        Container={IconContainer}
      />
      <ReminderEmail
        data={data}
        overviewData={overviewData}
        Container={IconContainer}
      />
      <DeletePecr
        Container={IconContainer}
        pecrId={pecrId}
        siteId={siteId}
        disabled={disableEditAndDeleteButton()}
      />
    </div>
  );
}

TrackingFileDetailRowItem.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  isButton: PropTypes.bool,
};

function TrackingFileDetailRowItem({
  title,
  value,
  prefix = null,
  suffix = null,
  isButton = false,
  isFile = false,
  pecrId = null,
  siteId = null,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  function handleFilterSite(site) {
    const params = new URLSearchParams(location.search);
    params.set('site', site);
    params.set('step', 'all');
    navigate({ search: params.toString() });
  }
  let item = null;

  if (isFile) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '8px',
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: 'black',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {value &&
            value.map(file => {
              return (
                <Typography
                  key={file}
                  onClick={() => {
                    onDownloadCostImpactClick(file, pecrId, siteId);
                  }}
                  sx={{
                    cursor: 'pointer',
                    textAlign: 'center',
                    color: '#0a58ca',
                    textDecoration: 'underline',
                    maxWidth: 300,
                    maxHeight: 100,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    lineHeight: 1.4,
                  }}
                >
                  {file}
                </Typography>
              );
            })}
        </div>
      </div>
    );
  }
  if (isButton) {
    item = (
      <Button
        type="primary"
        size="small"
        message={value}
        onClick={() => handleFilterSite(value)}
      />
    );
  } else {
    item = (
      <div
        style={{
          fontSize: '1rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={value}
      >
        {prefix}
        {value || 0}
        {suffix}
      </div>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontWeight: 600,
          fontSize: '0.875rem',
          color: 'black',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {title}
      </div>
      {item}
    </div>
  );
}

export default TrackingFileDetailsFilter;
